// Konfiguracija za custom bazu podataka
// Koristi eksterni API na evo.belkomliftovi.com

import { apiFetch } from './apiFetch';

interface DatabaseConfig {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
}

// API endpoint za vašu bazu podataka
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

class DatabaseService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    console.log('Database service initialized for production:', {
      baseUrl: this.baseUrl
    });
  }

  // Generic fetch method
  private async fetchData<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    console.log('🌐 [API] Fetching from:', url);

    const response = await apiFetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      mode: 'cors',
      ...options,
    });

    console.log('🌐 [API] Response status:', response.status, 'for', url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🌐 [API] Error:', { status: response.status, message: errorText });
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data = await response.json();
    console.log('🌐 [API] Response data:', data);
    return data;
  }

  // Employees
  async getEmployees() {
    return this.fetchData('/employees');
  }

  async getEmployeeById(id: string) {
    return this.fetchData(`/employees/${id}`);
  }

  async createEmployee(employee: any) {
    return this.fetchData('/employees', {
      method: 'POST',
      body: JSON.stringify(employee),
    });
  }

  async updateEmployee(id: string, employee: any) {
    return this.fetchData(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(employee),
    });
  }

  async deleteEmployee(id: string) {
    return this.fetchData(`/employees/${id}`, {
      method: 'DELETE',
    });
  }

  // Tasks
  async getTasks() {
    return this.fetchData('/tasks');
  }

  async getTaskById(id: string) {
    return this.fetchData(`/tasks/${id}`);
  }

  async createTask(task: any) {
    return this.fetchData('/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    });
  }

  async updateTask(id: string, task: any) {
    return this.fetchData(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(task),
    });
  }

  async deleteTask(id: string) {
    return this.fetchData(`/tasks/${id}`, {
      method: 'DELETE',
    });
  }

  // Tools
  async getTools() {
    return this.fetchData('/tools');
  }

  async getToolById(id: string) {
    return this.fetchData(`/tools/${id}`);
  }

  async createTool(tool: any) {
    return this.fetchData('/tools', {
      method: 'POST',
      body: JSON.stringify(tool),
    });
  }

  async updateTool(id: string, tool: any) {
    return this.fetchData(`/tools/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tool),
    });
  }

  async deleteTool(id: string) {
    return this.fetchData(`/tools/${id}`, {
      method: 'DELETE',
    });
  }

  // Task Comments
  async getTaskComments(taskId: string) {
    return this.fetchData(`/tasks/${taskId}/comments`);
  }

  async createTaskComment(taskId: string, comment: any) {
    return this.fetchData(`/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify(comment),
    });
  }

  // Task Edit History
  async getTaskEditHistory(taskId: string) {
    return this.fetchData(`/tasks/${taskId}/history`);
  }

  async createTaskEditHistory(taskId: string, history: any) {
    // Konvertuj frontend format u backend format
    const backendHistory = {
      employee_id: null, // Za sada null, kasnije možemo dodati employee_id
      action_type: history.action || 'edit',
      action_data: {
        user: history.user_name || history.user,
        details: history.details,
        timestamp: history.timestamp || new Date().toISOString()
      }
    };
    
    return this.fetchData(`/tasks/${taskId}/history`, {
      method: 'POST',
      body: JSON.stringify(backendHistory),
    });
  }

  // Tool History
  async getToolHistory(toolId: string) {
    return this.fetchData(`/tools/${toolId}/history`);
  }

  async createToolHistory(toolId: string, history: any) {
    return this.fetchData(`/tools/${toolId}/history`, {
      method: 'POST',
      body: JSON.stringify(history),
    });
  }

  // Authentication
  async login(email: string, password: string) {
    console.log('Attempting login with:', { email, password });
    return this.fetchData('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    return this.fetchData('/auth/logout', {
      method: 'POST',
    });
  }

  // Task Opening/Closing
  async openTask(taskId: string, employeeId: string, data: {
    vehicleId?: string;
    selectedTools?: string[];
    notes?: string;
  }) {
    return this.fetchData('/tasks/open', {
      method: 'POST',
      body: JSON.stringify({
        taskId,
        employeeId,
        ...data
      }),
    });
  }

  async closeTask(taskId: string, employeeId: string, data: {
    openingId: string;
    returnedVehicle?: boolean;
    returnedTools?: string[];
    workDescription?: string;
    notes?: string;
  }) {
    return this.fetchData('/tasks/close', {
      method: 'POST',
      body: JSON.stringify({
        taskId,
        employeeId,
        ...data
      }),
    });
  }

  async getTaskOpening(taskId: string, employeeId: string) {
    return this.fetchData(`/tasks/${taskId}/opening/${employeeId}`);
  }

  async addTaskOpeningStep(openingId: string, stepName: string, stepData?: any) {
    return this.fetchData('/tasks/opening/step', {
      method: 'POST',
      body: JSON.stringify({
        openingId,
        stepName,
        stepData
      }),
    });
  }

  async addTaskClosingStep(closingId: string, stepName: string, stepData?: any) {
    return this.fetchData('/tasks/closing/step', {
      method: 'POST',
      body: JSON.stringify({
        closingId,
        stepName,
        stepData
      }),
    });
  }

  async getTaskHistory(taskId: string) {
    return this.fetchData(`/tasks/${taskId}/history`);
  }

  // Task Acceptances
  async createTaskAcceptance(taskId: string, acceptedBy: string, acceptanceNote?: string) {
    return this.fetchData('/tasks/acceptances', {
      method: 'POST',
      body: JSON.stringify({
        taskId,
        acceptedBy,
        acceptanceNote
      }),
    });
  }

  async getTaskAcceptances(taskId: string) {
    return this.fetchData(`/tasks/${taskId}/acceptances`);
  }

  async createTaskRejection(taskId: string, rejectedBy: string, rejectionReason: string) {
    return this.fetchData('/tasks/rejections', {
      method: 'POST',
      body: JSON.stringify({
        taskId,
        rejectedBy,
        rejectionReason
      }),
    });
  }

  async getTaskRejections(taskId: string) {
    return this.fetchData(`/tasks/${taskId}/rejections`);
  }
}

export const db = new DatabaseService();